import React, {useEffect, useRef} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {twJoin} from "tailwind-merge";
import {ActionRow, ActionRowButton, defaultBorderColor, Modal, useNotifications} from "peggirkit";
import {api} from "@/data/api";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {Category} from "@/data/entities/menu";
import {ADMIN_MENU_QUERY_KEY, DAYS} from "@/data/entities/menuEditor";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import DayPlanningRow from "@widgets/menuEditor/menu/categories/category/planning/DayPlanningRow";
import {
    CategoryPlanningFormValues,
    toCategoryPlanningFormValues,
    toDayAvailabilities,
} from "@widgets/menuEditor/menu/categories/category/planning/categoryPlanning";

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    category: Category,
};

/**
 * When a category is shown, per day of the week.
 */
const CategoryPlanningModal = ({show, setShow, category}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const formMethods = useForm<CategoryPlanningFormValues>({
        defaultValues: toCategoryPlanningFormValues(category),
    });
    const {reset, handleSubmit} = formMethods;
    const wasShown = useRef(false);

    // Fill the form when the modal opens (not on a refetch while it's open)
    useEffect(() => {
        if (show && !wasShown.current) {
            reset(toCategoryPlanningFormValues(category));
        }
        wasShown.current = show;
    }, [show, category, reset]);

    const {mutate, isPending} = useMutation({
        // The category is updated as a whole: name, subtitle and products stay
        mutationFn: (form: CategoryPlanningFormValues) => api.menuEditor.updateCategory(
            dashboardLocation.id,
            category.id,
            {
                name: category.name,
                subtitle: category.subtitle,
                productIds: category.productIds,
                availability: toDayAvailabilities(form),
            },
        ),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [ADMIN_MENU_QUERY_KEY, dashboardLocation.id]});
            setShow(false);
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
    });
    const submit = handleSubmit((form: CategoryPlanningFormValues) => mutate(form));

    return (
        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: "planningOf"}, {category: category.name})}
            maxWidth={"max-w-2xl"}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setShow(false);
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"cancel"}/>
                    </ActionRowButton>

                    <ActionRowButton
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    void submit();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"saveAndClose"}/>
                    </ActionRowButton>
                </ActionRow>
            }
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <p className={"mt-2 text-sm text-gray-500 dark:text-slate-400"}>
                <FormattedMessage id={"planning.description"}/>
            </p>

            <FormProvider {...formMethods}>
                <form onSubmit={submit}>
                    <ol className={twJoin("w-full my-4 divide-y", defaultBorderColor, "divide-gray-200 dark:divide-slate-700")}>
                        {DAYS.map((day, index) =>
                            <DayPlanningRow
                                key={day}
                                index={index}
                                disabled={isPending}
                            />
                        )}
                    </ol>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default CategoryPlanningModal;